/**
 * Website detail popup (drawer + bottom sheet).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  BarChart3,
  BriefcaseBusiness,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  Info,
  Map as MapIcon,
  MapPin,
  Utensils,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  fetchWebsiteCoreDetail,
  fetchWebsiteSimilar,
  type CategoryPathEntry,
  type SimilarWebsiteDetail,
  type WebsiteCoreDetail,
} from "@/services/websiteDetail.service";
import { recordPageview } from "@/services/website.service";
import { RegionMiniMap } from "@/components/website-details/RegionMiniMap";
import { WebsiteRecommendationWorkbench } from "@/components/website-details/WebsiteRecommendationWorkbench";
import { buildSearchQueryString } from "@/app/searchNavigation";
import type { NavigateOptions } from "@/app/types";
import { toUiScaleFactor } from "@/utils/uiScale";
import { formatRegionLabel, formatRegionPath } from "@/utils/regionLabel";

type WebsiteDetailDrawerProps = {
  open: boolean;
  websiteId: string | null;
  onOpenChange: (open: boolean) => void;
  onSelectWebsite: (id: string) => void;
  navigate?: (page: string, options?: NavigateOptions) => void;
};

type TimedCacheEntry<T> = {
  value: T;
  createdAt: number;
};

type RelatedPanel = "similar" | "research";

const DETAIL_CACHE_TTL_MS = 2 * 60_000;
const DETAIL_CACHE_MAX_ENTRIES = 120;
const EMPTY_SIMILAR_WEBSITES: SimilarWebsiteDetail[] = [];

const setsEqual = (left: Set<string>, right: Set<string>) => {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
};

const getCachedValue = <T,>(
  cache: Map<string, TimedCacheEntry<T>>,
  key: string,
): T | null => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.createdAt > DETAIL_CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.value;
};

const setCachedValue = <T,>(
  cache: Map<string, TimedCacheEntry<T>>,
  key: string,
  value: T,
) => {
  cache.delete(key);
  cache.set(key, { value, createdAt: Date.now() });
  while (cache.size > DETAIL_CACHE_MAX_ENTRIES) {
    const firstKey = cache.keys().next().value;
    if (!firstKey) break;
    cache.delete(firstKey);
  }
};

const toCategoryLabel = (
  entry: CategoryPathEntry,
  language: string
): string => {
  const isGerman = language.toLowerCase().startsWith("de");
  const preferred = isGerman ? entry.nameDe : entry.nameEn;
  return preferred?.trim() || entry.nameDe || entry.nameEn || entry.slug || "";
};

const normalizeHostLabel = (hostname?: string | null, url?: string | null) => {
  if (hostname?.trim()) return hostname.trim();
  if (url?.trim()) return url.trim().replace(/^https?:\/\//, "");
  return "";
};

const mutedTextStyle = {
  color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
} as const;

const bodyTextStyle = {
  color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
} as const;

const sectionTitleStyle = {
  color: "color-mix(in srgb, var(--primary) 65%, var(--foreground))",
} as const;

export function WebsiteDetailDrawer({
  open,
  websiteId,
  onOpenChange,
  onSelectWebsite,
  navigate,
}: WebsiteDetailDrawerProps) {
  const { t, language, uiScale } = useApp();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const dateLocale = language === "de" ? "de-DE" : "en-US";
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(dateLocale),
    [dateLocale]
  );
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(dateLocale),
    [dateLocale]
  );
  const coordinateFormatter = useMemo(
    () => new Intl.NumberFormat(dateLocale, { maximumFractionDigits: 5 }),
    [dateLocale]
  );
  const uiScaleFactor = useMemo(() => toUiScaleFactor(uiScale), [uiScale]);
  const detailSheetStyle = useMemo(
    () =>
      ({
        "--detail-ui-scale": `${uiScaleFactor}`,
        left: "auto",
        right: 0,
      }) as CSSProperties,
    [uiScaleFactor]
  );

  const [website, setWebsite] = useState<WebsiteCoreDetail | null>(null);
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [websiteFailed, setWebsiteFailed] = useState(false);
  const [similarWebsites, setSimilarWebsites] = useState<SimilarWebsiteDetail[] | null>(null);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarFailed, setSimilarFailed] = useState(false);
  const [activeRelatedPanel, setActiveRelatedPanel] = useState<RelatedPanel>("similar");
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [expandedSimilarIds, setExpandedSimilarIds] = useState<Set<string>>(new Set());
  const [overflowingSimilarDescriptionIds, setOverflowingSimilarDescriptionIds] = useState<Set<string>>(new Set());
  const websiteCacheRef = useRef<Map<string, TimedCacheEntry<WebsiteCoreDetail>>>(new Map());
  const similarCacheRef = useRef<Map<string, TimedCacheEntry<SimilarWebsiteDetail[]>>>(new Map());
  const similarRailRef = useRef<HTMLDivElement | null>(null);

  const resolveEffectiveWidth = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }
    if (!Number.isFinite(uiScaleFactor) || uiScaleFactor <= 0) {
      return window.innerWidth;
    }
    return window.innerWidth / uiScaleFactor;
  }, [uiScaleFactor]);

  const resolveEffectiveHeight = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }
    if (!Number.isFinite(uiScaleFactor) || uiScaleFactor <= 0) {
      return window.innerHeight;
    }
    return window.innerHeight / uiScaleFactor;
  }, [uiScaleFactor]);

  const [splitDetailsLayout, setSplitDetailsLayout] = useState(() => {
    const width = resolveEffectiveWidth();
    if (width === null) return false;
    return width >= 1024;
  });
  const [effectiveHeight, setEffectiveHeight] = useState(() => {
    const height = resolveEffectiveHeight();
    return height ?? 0;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const compute = () => {
      const width = resolveEffectiveWidth();
      const height = resolveEffectiveHeight();
      setSplitDetailsLayout(width !== null && width >= 1024);
      setEffectiveHeight(height ?? 0);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [resolveEffectiveHeight, resolveEffectiveWidth]);

  useEffect(() => {
    if (typeof document === "undefined" || !open) return;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    document.body.classList.add("website-detail-page-open");
    window.scrollTo({ left: 0, top: 0 });
    return () => {
      document.body.classList.remove("website-detail-page-open");
      window.scrollTo({ left: scrollX, top: scrollY });
    };
  }, [open]);

  const compactVerticalLayout =
    splitDetailsLayout && effectiveHeight > 0 && effectiveHeight < 1120;

  useEffect(() => {
    if (!open || !websiteId) return;

    setWebsiteFailed(false);
    setSimilarFailed(false);
    setSimilarWebsites(null);
    setSimilarLoading(false);
    setActiveRelatedPanel("similar");
    setDescriptionExpanded(false);
    setExpandedSimilarIds(new Set());
    setOverflowingSimilarDescriptionIds(new Set());

    const cachedWebsite = getCachedValue(websiteCacheRef.current, websiteId);
    if (cachedWebsite) {
      setWebsite(cachedWebsite);
      setWebsiteLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setWebsite(null);
    setWebsiteLoading(true);

    fetchWebsiteCoreDetail(websiteId, { signal: controller.signal })
      .then((data) => {
        if (cancelled) return;
        setCachedValue(websiteCacheRef.current, websiteId, data);
        setWebsite(data);
      })
      .catch(() => {
        if (cancelled) return;
        setWebsiteFailed(true);
        setWebsite(null);
      })
      .finally(() => {
        if (cancelled) return;
        setWebsiteLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, websiteId]);

  useEffect(() => {
    if (!open || !websiteId || !website || website.id !== websiteId) return;

    const cachedSimilar = getCachedValue(similarCacheRef.current, websiteId);
    if (cachedSimilar) {
      setSimilarWebsites(cachedSimilar);
      setSimilarLoading(false);
      setSimilarFailed(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setSimilarLoading(true);
    setSimilarFailed(false);

    fetchWebsiteSimilar(websiteId, { signal: controller.signal })
      .then((items) => {
        if (cancelled) return;
        const sliced = items.slice(0, 5);
        setCachedValue(similarCacheRef.current, websiteId, sliced);
        setSimilarWebsites(sliced);
      })
      .catch(() => {
        if (cancelled) return;
        setSimilarFailed(true);
        setSimilarWebsites([]);
      })
      .finally(() => {
        if (cancelled) return;
        setSimilarLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, websiteId, website]);

  const similarList = similarWebsites ?? EMPTY_SIMILAR_WEBSITES;
  const similarCount = similarList.length;
  const categoryPath = website?.categoryPath ?? [];

  useEffect(() => {
    if (!open || !similarCount) {
      setOverflowingSimilarDescriptionIds((current) =>
        current.size === 0 ? current : new Set()
      );
      return;
    }

    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const next = new Set<string>();
      document.querySelectorAll<HTMLElement>("[data-similar-description-id]").forEach((node) => {
        const id = node.dataset.similarDescriptionId;
        if (!id || expandedSimilarIds.has(id)) return;
        if (node.scrollHeight > node.clientHeight + 1) {
          next.add(id);
        }
      });
      setOverflowingSimilarDescriptionIds((current) =>
        setsEqual(current, next) ? current : next
      );
    };

    const frame = window.requestAnimationFrame(measure);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [open, similarCount, similarList, splitDetailsLayout, uiScale, expandedSimilarIds]);

  const regionPathLabel = useMemo(() => {
    const names = formatRegionPath(
      website?.regionPathNames ?? [],
      website?.regionPathNamesEn ?? []
    );
    return names.filter(Boolean).join(" > ");
  }, [website?.regionPathNames, website?.regionPathNamesEn]);

  const metadata = useMemo(() => {
    if (!website) {
      return [] as Array<{ label: string; value: string }>;
    }
    const rows: Array<{ label: string; value: string }> = [];
    const addressValue = website.address?.trim() ?? "";
    const zipValue = website.zipCode?.trim() ?? "";
    const zipDigits = zipValue.replace(/\D/g, "");
    const addressDigits = addressValue.replace(/\D/g, "");
    const addressHasZip =
      !!addressValue &&
      !!zipValue &&
      (addressValue.includes(zipValue) ||
        (zipDigits && addressDigits.includes(zipDigits)));

    if (website.urlLandingPage) rows.push({ label: t("websiteDetails.url"), value: website.urlLandingPage });
    if (website.hostname) rows.push({ label: t("websiteDetails.hostname"), value: website.hostname });
    if (addressValue) rows.push({ label: t("websiteDetails.address"), value: addressValue });
    if (zipValue && (!addressValue || !addressHasZip)) {
      rows.push({ label: t("websiteDetails.zipCode"), value: zipValue });
    }
    if (website.geoLat != null && website.geoLon != null) {
      rows.push({
        label: t("websiteDetails.coordinates"),
        value: `${coordinateFormatter.format(website.geoLat)} / ${coordinateFormatter.format(website.geoLon)}`,
      });
    }
    if (website.addedAt) {
      rows.push({
        label: t("websiteDetails.addedAt"),
        value: dateFormatter.format(new Date(website.addedAt)),
      });
    }
    if (website.addedBy) rows.push({ label: t("websiteDetails.addedBy"), value: website.addedBy });
    return rows;
  }, [t, website, dateFormatter, coordinateFormatter]);

  const hostnameLabel = useMemo(() => {
    if (!website) return "";
    return normalizeHostLabel(website.hostname, website.urlLandingPage);
  }, [website]);

  const hostnameHref = useMemo(() => {
    if (!website) return undefined;
    const landing = website.urlLandingPage?.trim();
    if (landing) return landing;
    const host = website.hostname?.trim();
    if (!host) return undefined;
    return /^https?:\/\//i.test(host) ? host : `https://${host}`;
  }, [website]);

  const openWebsite = () => {
    if (!website?.urlLandingPage) return;
    recordPageview(website.id);
    window.open(website.urlLandingPage, "_blank", "noopener,noreferrer");
  };

  const scrollSimilarRail = (direction: -1 | 1) => {
    const rail = similarRailRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.78, 280),
      behavior: "smooth",
    });
  };

  const openFilteredCategories = useCallback(
    (payload: { categorySlugs?: string[]; regionKey?: string | null }) => {
      const search = buildSearchQueryString({
        categorySlugs: payload.categorySlugs ?? [],
        regions: payload.regionKey ? [payload.regionKey] : [],
      });
      onOpenChange(false);
      if (navigate) {
        navigate("categories", {
          search,
          state: { focusTarget: "categories" },
        });
        return;
      }
      const target = search ? `/categories?${search}` : "/categories";
      window.location.assign(target);
    },
    [navigate, onOpenChange]
  );

  const normalizePostalRegionSegment = (value: string) => {
    const digits = value.replace(/\D+/g, "");
    if (digits.length === 4) return `0${digits}`;
    return digits.length === 5 ? digits : value.trim();
  };

  const buildRegionKeyForPath = (pathNames: string[], endIndex: number) => {
    const path = pathNames
      .slice(0, endIndex + 1)
      .map((entry) => entry?.trim())
      .filter((entry): entry is string => !!entry);
    if (path.length === 0) return null;
    const stateName = path[0];
    if (!stateName) return null;
    if (path.length === 1) return `federalStates:${stateName}:${stateName}`;
    if (path.length === 2) return `administrativeDistricts:${stateName}:${path[1]}`;
    if (path.length === 3) return `districts:${stateName}:${path[2]}`;
    return `postalCodes:${stateName}:${normalizePostalRegionSegment(path[path.length - 1])}`;
  };

  const buildRegionCrumbs = (
    displayNames: string[],
    keyNames: string[]
  ) =>
    displayNames
      .map((label, index) => ({
        label,
        onClick: () => {
          const regionKey = buildRegionKeyForPath(keyNames, index);
          if (regionKey) openFilteredCategories({ regionKey });
        },
      }))
      .filter((crumb) => crumb.label.trim().length > 0);

  const categoryPathCrumbs = categoryPath.map((entry, index) => ({
    label: toCategoryLabel(entry, language),
    onClick: () =>
      openFilteredCategories({
        categorySlugs: categoryPath
          .slice(0, index + 1)
          .map((pathEntry) => pathEntry.slug),
      }),
  }));

  const regionPathCrumbs = buildRegionCrumbs(
    formatRegionPath(
      website?.regionPathNames ?? [],
      website?.regionPathNamesEn ?? []
    ),
    website?.regionPathNames ?? []
  );

  const renderPathCrumbs = (
    crumbs: Array<{ label: string; onClick: () => void }>,
    className = ""
  ) => (
    <div className={`flex min-w-0 flex-1 flex-wrap items-center gap-x-1 gap-y-0.5 overflow-visible ${className}`}>
      {crumbs.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`} className="flex min-w-0 max-w-full items-center gap-1">
          <button
            type="button"
            className="min-w-0 max-w-full break-words rounded-sm text-left transition-colors hover:!text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            title={crumb.label}
            onClick={(event) => {
              event.stopPropagation();
              crumb.onClick();
            }}
          >
            {crumb.label}
          </button>
          {index < crumbs.length - 1 && (
            <span className="shrink-0 opacity-65" aria-hidden="true">
              &gt;
            </span>
          )}
        </span>
      ))}
    </div>
  );

  const renderHeaderSkeleton = () => (
    <div className="space-y-3 pt-2">
      <Skeleton className="h-6 w-64" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-9 w-36 rounded-md" />
    </div>
  );

  const renderWebsiteBodySkeleton = () => (
    <div className="space-y-4">
      <Card className="stat-card">
        <CardContent className="space-y-2 p-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </CardContent>
      </Card>
      <div className={splitDetailsLayout ? "grid gap-3 grid-cols-2" : "grid gap-3"}>
        <Skeleton className="h-44 rounded-md" />
        <Skeleton className="h-44 rounded-md" />
      </div>
    </div>
  );

  const renderSimilarSkeleton = () => (
    <div className="flex gap-3 overflow-hidden">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={`similar-skeleton-${index}`} className="w-[17.5rem] shrink-0 rounded-md border bg-card">
          <div className="space-y-3 p-3.5">
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderInfoTile = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    onOpenPath?: () => void,
  ) => (
    <div className="group flex min-w-0 items-center gap-3 border-t border-border/70 px-1 py-2 transition-colors first:border-t-0 hover:bg-muted/10">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center text-primary">
        {icon}
      </div>
      <div className="grid min-w-0 flex-1 gap-0.5 sm:flex sm:items-center sm:gap-4">
        <div className="w-24 shrink-0 text-xs font-semibold" style={sectionTitleStyle}>
          {label}
        </div>
        <div className="min-w-0 text-xs leading-snug" style={bodyTextStyle}>
          {value}
        </div>
      </div>
      {onOpenPath && (
      <button
        type="button"
        className="shrink-0 rounded-sm opacity-55 transition-transform hover:translate-x-0.5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          onClick={(event) => {
            event.stopPropagation();
            onOpenPath();
          }}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  const renderSimilarMeta = (
    icon: React.ReactNode,
    crumbs: Array<{ label: string; onClick: () => void }>,
    onOpenPath: () => void,
    className = ""
  ) => (
    <div
      className={`flex min-w-0 items-start gap-1.5 rounded-md ${className}`}
      style={{
        ...mutedTextStyle,
        fontSize: "0.625rem",
        lineHeight: "0.85rem",
      }}
    >
      <span className="mt-px shrink-0">{icon}</span>
      <span
        className="min-w-0 flex-1 overflow-hidden"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {crumbs.map((crumb, index) => (
          <span key={`${crumb.label}-${index}`}>
            <button
              type="button"
              className="rounded-sm text-left font-medium transition-colors hover:!text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              style={{
                color: "inherit",
                fontSize: "inherit",
                lineHeight: "inherit",
              }}
              title={crumb.label}
              onClick={(event) => {
                event.stopPropagation();
                crumb.onClick();
              }}
            >
              {crumb.label}
            </button>
            {index < crumbs.length - 1 && (
              <span className="px-0.5 opacity-60" aria-hidden="true">
                &gt;
              </span>
            )}
          </span>
        ))}
      </span>
      <button
        type="button"
        className="ml-auto mt-px shrink-0 rounded-sm opacity-60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        style={{
          color: "inherit",
          fontSize: "inherit",
          lineHeight: "inherit",
        }}
        onClick={(event) => {
          event.stopPropagation();
          onOpenPath();
        }}
      >
        <ChevronRight className="h-2.5 w-2.5" />
      </button>
    </div>
  );

  const categoryIconFor = (label: string) => {
    const normalized = label.toLowerCase();
    if (normalized.includes("food") || normalized.includes("drink") || normalized.includes("cafe")) {
      return <Utensils className="h-3.5 w-3.5 shrink-0 text-primary" />;
    }
    return <BriefcaseBusiness className="h-3.5 w-3.5 shrink-0 text-primary" />;
  };

  const renderSimilarCard = (similar: SimilarWebsiteDetail, index: number) => {
    const similarCategoryPath = similar.categoryPath ?? [];
    const similarCategoryCrumbs = similarCategoryPath.map((entry, crumbIndex) => ({
      label: toCategoryLabel(entry, language),
      onClick: () =>
        openFilteredCategories({
          categorySlugs: similarCategoryPath
            .slice(0, crumbIndex + 1)
            .map((pathEntry) => pathEntry.slug),
        }),
    }));
    const similarRegionDisplayNames = formatRegionPath(
      similar.regionPathNames ?? [],
      similar.regionPathNamesEn ?? []
    );
    const similarRegionKeyNames = similar.regionPathNames ?? [];
    const similarRegionCrumbs = buildRegionCrumbs(
      similarRegionDisplayNames,
      similarRegionKeyNames
    );
    const similarUrl = similar.urlLandingPage ?? undefined;
    const similarHost = normalizeHostLabel(similar.hostname, similar.urlLandingPage);
    const primaryLabel = similar.title || similarHost || similar.id;
    const isExpanded = expandedSimilarIds.has(similar.id);
    const hasAnyExpandedSimilar = expandedSimilarIds.size > 0;
    const descriptionOverflows = overflowingSimilarDescriptionIds.has(similar.id);
    const cardWidth = splitDetailsLayout ? "21.5rem" : "19.5rem";
    const cardHeight = hasAnyExpandedSimilar
      ? "25.375rem"
      : compactVerticalLayout
        ? "16.875rem"
        : "19.125rem";

    return (
      <article
        key={similar.id}
        className="flex h-full min-w-0 shrink-0 flex-col overflow-hidden rounded-md border bg-card shadow-sm transition-colors hover:bg-muted/10"
        style={{
          width: cardWidth,
          minWidth: cardWidth,
          maxWidth: cardWidth,
          flexBasis: cardWidth,
          height: cardHeight,
          minHeight: cardHeight,
          maxHeight: cardHeight,
          padding: compactVerticalLayout ? "0.8rem" : "1rem",
        }}
      >
        <div className="mb-2 flex items-start gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold text-primary">
            {index + 1}
          </span>
          <button
            type="button"
            className="min-w-0 flex-1 text-left outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => onSelectWebsite(similar.id)}
          >
            <div
              className="line-clamp-2 font-semibold"
              style={{
                ...bodyTextStyle,
                fontSize: "0.75rem",
                lineHeight: "1rem",
              }}
            >
              {primaryLabel}
            </div>
          </button>
          <div className="flex shrink-0 items-center gap-1">
            {similar.pageviewsTotal != null && (
              <Badge
                variant="secondary"
                className="h-5 shrink-0 gap-0.5 border-0 bg-transparent px-0 tabular-nums shadow-none"
                style={{ ...mutedTextStyle, fontSize: "0.5625rem", lineHeight: "0.7rem" }}
              >
                <Eye className="h-2.5 w-2.5" />
                {numberFormatter.format(similar.pageviewsTotal)}
              </Badge>
            )}
            {similarUrl && (
              <Button
                variant="outline"
                size="icon"
                aria-label={`${t("websiteDetails.openWebsite")}: ${
                  similar.title || similarHost || similar.id
                }`}
                className="h-6 w-6 shrink-0 rounded-md"
                onClick={(event) => {
                  event.stopPropagation();
                  recordPageview(similar.id);
                  window.open(similarUrl, "_blank", "noopener,noreferrer");
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {similarHost && similarUrl ? (
          <a
            href={similarUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={similarHost}
            className="mb-2 truncate font-semibold text-primary underline decoration-dotted underline-offset-2 hover:opacity-80"
            style={{ fontSize: "0.6875rem", lineHeight: "0.9rem" }}
            onClick={() => recordPageview(similar.id)}
          >
            {similarHost}
          </a>
        ) : similarHost ? (
          <div
            className="mb-2 truncate font-semibold text-primary"
            style={{ fontSize: "0.6875rem", lineHeight: "0.9rem" }}
          >
            {similarHost}
          </div>
        ) : null}

        <div className="grid h-[3.9rem] min-w-0 gap-1.5 overflow-hidden text-left">
          {similarCategoryCrumbs.length > 0
            ? renderSimilarMeta(
                categoryIconFor(similarCategoryCrumbs.map((crumb) => crumb.label).join(" ")),
                similarCategoryCrumbs,
                () =>
                  openFilteredCategories({
                    categorySlugs: similarCategoryPath.map((pathEntry) => pathEntry.slug),
                  }),
                "h-[1.65rem] overflow-hidden"
              )
            : null}
          {similarRegionCrumbs.length > 0
            ? renderSimilarMeta(
                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />,
                similarRegionCrumbs,
                () => {
                  const regionKey = buildRegionKeyForPath(
                    similarRegionKeyNames,
                    similarRegionKeyNames.length - 1
                  );
                  if (regionKey) openFilteredCategories({ regionKey });
                },
                "h-[1.65rem] overflow-hidden"
              )
            : null}
        </div>

        {similar.description && (
          <div
            data-similar-description-id={similar.id}
            className="mt-3 min-h-0 flex-1"
            style={{
              ...mutedTextStyle,
              display: "-webkit-box",
              fontSize: "0.6875rem",
              lineHeight: "1rem",
              WebkitLineClamp: isExpanded ? 10 : 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {similar.description}
          </div>
        )}

        {similar.description && (isExpanded || descriptionOverflows) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-auto h-6 w-fit gap-1 px-0 text-primary hover:bg-transparent"
            style={{ fontSize: "0.6875rem", lineHeight: "0.9rem" }}
            onClick={() =>
              setExpandedSimilarIds((current) => {
                const next = new Set(current);
                if (next.has(similar.id)) next.delete(similar.id);
                else next.add(similar.id);
                return next;
              })
            }
          >
            {isExpanded ? t("common.showLess") : t("common.showMore")}
            <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
          </Button>
        )}
      </article>
    );
  };

  const renderSimilarContent = () => {
    if (similarLoading && similarCount === 0) return renderSimilarSkeleton();
    if (similarFailed) {
      return (
        <div className="rounded-md border p-3 text-sm text-destructive">
          {t("websiteDetails.similarFailed")}
        </div>
      );
    }
    if (!similarCount) {
      return (
        <div className="rounded-md border p-3 text-sm" style={mutedTextStyle}>
          {t("websiteDetails.noSimilar")}
        </div>
      );
    }
    const railHeight =
      expandedSimilarIds.size > 0
        ? "26.125rem"
        : compactVerticalLayout
          ? "17.625rem"
          : "19.875rem";
    return (
      <div className="relative min-w-0">
        {similarCount > 1 && (
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t("common.prev")}
              className="absolute -left-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 rounded-full bg-card shadow-md md:flex"
              onClick={() => scrollSimilarRail(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t("common.next")}
              className="absolute -right-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 rounded-full bg-card shadow-md md:flex"
              onClick={() => scrollSimilarRail(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
        <div
          ref={similarRailRef}
          className="flex min-w-0 snap-x items-stretch gap-4 overflow-x-auto overscroll-x-contain pb-2"
          style={{ scrollbarWidth: "thin", height: railHeight }}
        >
          {similarList.map((item, index) => (
            <div key={item.id} className="h-full shrink-0 snap-start">
              {renderSimilarCard(item, index)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="website-detail-sheet flex h-dvh max-h-dvh w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-none max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:left-0 max-sm:right-0 max-sm:h-[92dvh] max-sm:rounded-t-2xl max-sm:border-l-0 max-sm:border-t [&>button]:right-3 [&>button]:top-3 [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-md [&>button]:border [&>button]:border-primary/35 [&>button]:bg-primary/12 [&>button]:text-foreground [&>button]:opacity-100 [&>button]:shadow-md [&>button]:hover:bg-primary/20 [&>button]:hover:opacity-100 [&>button>svg]:h-5 [&>button>svg]:w-5"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        style={detailSheetStyle}
      >
        <SheetHeader
          className={`shrink-0 overflow-hidden border-b bg-background px-5 sm:px-6 lg:px-10 xl:px-12 ${
            compactVerticalLayout ? "py-2" : "py-3"
          }`}
        >
          <SheetTitle className="flex items-center gap-2 text-base text-primary-heading">
            <Info className="h-4 w-4" />
            {t("websiteDetails.title")}
          </SheetTitle>

          {websiteLoading ? (
            renderHeaderSkeleton()
          ) : (
            !websiteFailed && website && (
              <div className="space-y-2 overflow-hidden pt-1">
                <div className="flex min-w-0 items-start justify-between gap-3 overflow-hidden">
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div
                      className="break-words text-lg font-semibold leading-snug"
                      style={{
                        ...bodyTextStyle,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {website.title || hostnameLabel || website.urlLandingPage}
                    </div>
                    {hostnameLabel &&
                      (hostnameHref ? (
                        <a
                          href={hostnameHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={hostnameLabel}
                          className="mt-1 block truncate text-sm font-medium underline decoration-dotted underline-offset-2 hover:opacity-80"
                          style={sectionTitleStyle}
                          onClick={() => recordPageview(website.id)}
                        >
                          {hostnameLabel}
                        </a>
                      ) : (
                        <div className="mt-1 truncate text-sm font-medium" style={sectionTitleStyle}>
                          {hostnameLabel}
                        </div>
                      ))}
                  </div>
                  {website.status && (
                    <Badge variant="secondary" className="shrink-0">
                      {website.status}
                    </Badge>
                  )}
                </div>

                {website.urlLandingPage && (
                  <Button
                    variant="outline"
                    className={`${compactVerticalLayout ? "h-8" : "h-9"} w-full justify-center gap-2 sm:w-auto`}
                    onClick={openWebsite}
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t("websiteDetails.openWebsite")}
                  </Button>
                )}
              </div>
            )
          )}
        </SheetHeader>

        <div className="website-detail-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
          <div
            className={`px-4 sm:px-6 lg:px-10 xl:px-12 ${
              compactVerticalLayout ? "space-y-3 py-3" : "space-y-5 py-5"
            }`}
          >
            {websiteLoading && renderWebsiteBodySkeleton()}

            {websiteFailed && (
              <div className="rounded-md border p-3 text-sm text-destructive">
                {t("websiteDetails.failed")}
              </div>
            )}

            {!websiteLoading && !websiteFailed && website && (
              <>
                <section
                  className={`grid items-stretch ${compactVerticalLayout ? "gap-3" : "gap-4"}`}
                  style={splitDetailsLayout ? { gridTemplateColumns: "minmax(0, 3fr) minmax(22rem, 2fr)" } : undefined}
                >
                  <div className={`min-w-0 ${compactVerticalLayout ? "space-y-2" : "space-y-3"}`}>
                    {website.description && (
                      <section className={`rounded-md border bg-card ${compactVerticalLayout ? "p-3" : "p-4"}`}>
                        <div className={`${compactVerticalLayout ? "mb-1" : "mb-2"} flex items-center gap-2 text-sm font-medium`} style={sectionTitleStyle}>
                          <FileText className="h-4 w-4" />
                          {t("website.description")}
                        </div>
                        <div
                          className="text-sm leading-relaxed"
                          style={{
                            ...bodyTextStyle,
                            display: "-webkit-box",
                            WebkitLineClamp: descriptionExpanded ? "unset" : splitDetailsLayout ? 4 : 6,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {website.description}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={`${compactVerticalLayout ? "mt-1 h-6" : "mt-2 h-7"} gap-1 px-0 text-xs text-primary hover:bg-transparent`}
                          onClick={() => setDescriptionExpanded((value) => !value)}
                        >
                          {descriptionExpanded ? t("common.showLess") : t("common.showMore")}
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${descriptionExpanded ? "rotate-180" : ""}`} />
                        </Button>
                      </section>
                    )}

                    <Card className="rounded-md border bg-card">
                      <CardContent className={`${compactVerticalLayout ? "space-y-2 p-3" : "space-y-3 p-4"}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-sm font-medium" style={sectionTitleStyle}>
                            <Globe className="h-4 w-4" />
                            {t("websiteDetails.metadata")}
                          </div>
                          {website.pageviewsTotal != null && (
                            <Badge variant="outline" className="shrink-0 gap-1">
                              <Eye className="h-3 w-3" />
                              {numberFormatter.format(website.pageviewsTotal)}
                            </Badge>
                          )}
                        </div>
                        <dl className="grid gap-x-5 gap-y-2 text-sm xl:grid-cols-2">
                          {metadata.map((row) => (
                            <div key={row.label} className="flex min-w-0 items-center gap-3">
                              <dt className="w-28 shrink-0 text-xs font-medium leading-snug" style={mutedTextStyle}>
                                {row.label}
                              </dt>
                              <dd className="min-w-0 flex-1 break-words leading-snug" style={bodyTextStyle}>
                                {row.value}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        {(categoryPath.length > 0 || website.regionName || website.regionNameEn || regionPathLabel) && (
                          <div className="grid gap-2 pt-1">
                            {categoryPath.length > 0 &&
                              renderInfoTile(
                                categoryIconFor(categoryPathCrumbs.map((crumb) => crumb.label).join(" ")),
                                t("websiteDetails.categoryPath"),
                                renderPathCrumbs(categoryPathCrumbs),
                                () =>
                                  openFilteredCategories({
                                    categorySlugs: categoryPath.map((entry) => entry.slug),
                                  })
                              )}
                            {(website.regionName || website.regionNameEn || regionPathLabel) &&
                              renderInfoTile(
                                <MapPin className="h-3.5 w-3.5" />,
                                t("websiteDetails.regionPath"),
                                regionPathCrumbs.length > 0
                                  ? renderPathCrumbs(regionPathCrumbs)
                                  : formatRegionLabel(website.regionName, website.regionNameEn),
                                () => {
                                  const regionKey = buildRegionKeyForPath(
                                    website.regionPathNames ?? [],
                                    (website.regionPathNames ?? []).length - 1
                                  );
                                  if (regionKey) openFilteredCategories({ regionKey });
                                }
                              )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="min-w-0">
                    <Card className="h-full rounded-md border bg-card">
                      <CardContent className={`flex h-full flex-col ${compactVerticalLayout ? "gap-2 p-3" : "gap-3 p-4"}`}>
                        <div className="flex items-center gap-2 text-sm font-medium" style={sectionTitleStyle}>
                          <MapIcon className="h-4 w-4" />
                          {t("websiteDetails.regionMap")}
                        </div>
                        <RegionMiniMap
                          regionLayerId={website.regionLayerId ?? undefined}
                          regionStateName={website.regionStateName ?? undefined}
                          regionName={website.regionName ?? undefined}
                          regionKey={website.regionKey ?? undefined}
                          geoLat={website.geoLat ?? undefined}
                          geoLon={website.geoLon ?? undefined}
                          emptyLabel={t("websiteDetails.regionMapUnavailable")}
                          className={`${compactVerticalLayout ? "min-h-[18rem]" : "min-h-[21rem]"} flex-1`}
                          aspectRatio={splitDetailsLayout ? "16 / 9" : "2 / 1"}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </section>

                <section className={`min-w-0 rounded-md border bg-card ${compactVerticalLayout ? "p-4" : "mt-2 p-6"}`}>
                  <div className={`${compactVerticalLayout ? "mb-2" : "mb-3"} flex items-start justify-between gap-3`}>
                    <div className="flex min-w-0 gap-2">
                      <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <div className="text-base font-semibold" style={sectionTitleStyle}>
                          {isAdmin ? t("websiteDetails.relatedContent") : t("websiteDetails.similarWebsites")}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0">{similarCount}</Badge>
                  </div>

                  {isAdmin && websiteId ? (
                    <Tabs
                      value={activeRelatedPanel}
                      onValueChange={(value) => setActiveRelatedPanel(value as RelatedPanel)}
                      className="min-w-0 gap-3"
                    >
                      <TabsList className="grid h-auto w-full min-w-0 grid-cols-2 gap-1 p-1">
                        <TabsTrigger value="similar" className="min-w-0 gap-2 px-2 text-xs sm:text-sm">
                          <Globe className="h-4 w-4 shrink-0" />
                          <span className="min-w-0 truncate">{t("websiteDetails.similarTab")}</span>
                        </TabsTrigger>
                        <TabsTrigger value="research" className="min-w-0 gap-2 px-2 text-xs sm:text-sm">
                          <BarChart3 className="h-4 w-4 shrink-0" />
                          <span className="min-w-0 truncate">{t("websiteDetails.researchComparisonTab")}</span>
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent
                        value="similar"
                        className="min-w-0"
                        forceMount
                        hidden={activeRelatedPanel !== "similar"}
                      >
                        {renderSimilarContent()}
                      </TabsContent>
                      <TabsContent
                        value="research"
                        className="min-w-0"
                        forceMount
                        hidden={activeRelatedPanel !== "research"}
                      >
                        <WebsiteRecommendationWorkbench websiteId={websiteId} />
                      </TabsContent>
                    </Tabs>
                  ) : (
                    renderSimilarContent()
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
